import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';
import { Button } from './ui/button';
import { toast } from 'sonner';
import {
  FileSignature, Send, Loader2, Eye, CheckCircle2, Clock, Link2,
  RotateCw, XCircle, FileDown, Mail, BellRing, BellOff,
} from 'lucide-react';
import { SendNdaDialog } from './SendNdaDialog';
import { NdaRemarksPanel } from './NdaRemarksPanel';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const fmtDt = (iso) => iso
  ? new Date(iso).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
  : null;

function StatusStep({ done, icon: Icon, label, ts, testid }) {
  return (
    <span data-testid={testid}
      className={`inline-flex items-center gap-1 text-[10px] font-mono border rounded px-1.5 py-0.5 ${
        done ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300' : 'border-border bg-secondary/40 text-muted-foreground'}`}>
      <Icon className="w-3 h-3" /> {label}{ts ? ` · ${ts}` : ''}
    </span>
  );
}

export function NdaCard({ projectId, project, isAdmin, isAcMember, onChanged }) {
  const [ndas, setNdas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [busy, setBusy] = useState(null);
  const [params] = useSearchParams();
  const highlightId = params.get('nda_remind');
  const highlightRef = useRef(null);

  const load = useCallback(() => {
    api.listNdas(projectId)
      .then(r => setNdas(r.data.ndas || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [projectId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!loading && highlightId && highlightRef.current) highlightRef.current.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [loading, highlightId]);

  const isBrokered = project?.deal_type === 'brokered';
  const isNdaNext = ['first_call_meeting'].includes(project?.pipeline_stage);
  const showForStage = (isBrokered
    ? ['ai_reviewed', 'olta_reviewed', 'olta_approved', 'outreach_wip', 'contacted', 'response_received', 'first_call_meeting', 'nda']
    : ['contacted', 'response_received', 'first_call_meeting', 'nda']).includes(project?.pipeline_stage);
  const hasSigned = ndas.some(n => n.status === 'signed');
  if (isAcMember) return null;
  if (!showForStage && ndas.length === 0) return null;

  const copyLink = (n) => {
    navigator.clipboard.writeText(`${window.location.origin}/nda/${n.token}`);
    toast.success('Public NDA link copied');
  };

  const resend = async (n) => {
    setBusy(n.id);
    try { await api.resendNda(n.id); toast.success(`NDA re-sent to ${n.recipient.email}`); load(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    setBusy(null);
  };

  const cancel = async (n) => {
    if (!window.confirm('Cancel this NDA? The public link becomes invalid.')) return;
    setBusy(n.id);
    try { await api.cancelNda(n.id); toast.success('NDA cancelled'); load(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    setBusy(null);
  };

  const remind = async (n) => {
    setBusy(n.id);
    try { await api.remindNda(n.id); toast.success(`Erinnerung an ${n.recipient.email} gesendet`); load(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Fehlgeschlagen'); }
    setBusy(null);
  };

  const dismissReminder = async (n) => {
    setBusy(n.id);
    try { await api.dismissNdaReminder(n.id); toast.success('Keine Erinnerung für diese NDA'); load(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Fehlgeschlagen'); }
    setBusy(null);
  };

  return (
    <div className={`bg-card border rounded-sm p-4 ${isNdaNext && !hasSigned ? 'border-teal-500/50' : 'border-border'}`} data-testid="nda-card">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <p className="font-heading text-base text-foreground flex items-center gap-2">
          <FileSignature className="w-4 h-4 text-teal-400" /> NDA
          {isBrokered && (
            <span data-testid="nda-olta-template-badge" title="OLTA-Vorlage — kann dem Broker schon vor OLTA Approved gesendet werden"
              className="text-[9px] font-mono border border-amber-500/40 bg-amber-500/10 text-amber-300 rounded px-1.5 py-0.5 uppercase">
              Unser NDA an Broker
            </span>
          )}
          {isNdaNext && !hasSigned && (
            <span className="text-[9px] font-mono border border-teal-500/40 bg-teal-500/10 text-teal-300 rounded px-1.5 py-0.5 uppercase">Next stage</span>
          )}
        </p>
        {isAdmin && !hasSigned && (
          <Button size="sm" onClick={() => setDialogOpen(true)} data-testid="send-nda-btn"
            className="bg-teal-600 hover:bg-teal-500 text-white rounded-sm text-xs h-8 px-3">
            <Send className="w-3.5 h-3.5 mr-1.5" /> Send NDA
          </Button>
        )}
      </div>
      {isNdaNext && !hasSigned && ndas.length === 0 && (
        <p className="text-xs text-muted-foreground mb-2">
          Next step: send the confidentiality agreement for e-signature — pre-filled from the deal, signed online by the owner, saved here automatically.
        </p>
      )}
      {loading ? (
        <div className="flex justify-center py-3"><Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /></div>
      ) : ndas.length === 0 ? (
        !isAdmin && <p className="text-xs text-muted-foreground">No NDA sent yet — an admin can send one.</p>
      ) : (
        <div className="space-y-2">
          {ndas.map((n, i) => (
            <div key={n.id} ref={n.id === highlightId ? highlightRef : null}
              className={`border bg-secondary/20 rounded-sm px-3 py-2 ${n.id === highlightId ? 'border-amber-500/70 ring-1 ring-amber-500/40' : 'border-border/60'}`}
              data-testid={`nda-row-${i}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs text-foreground font-medium">{n.fields?.company_name || n.project_name}</span>
                <span className="text-[10px] text-muted-foreground flex items-center gap-1"><Mail className="w-3 h-3" />{n.recipient?.name} · {n.recipient?.email}</span>
                {(n.revision || 1) > 1 && (
                  <span className="text-[9px] font-mono border border-violet-500/40 bg-violet-500/10 text-violet-300 rounded px-1.5 py-0.5 uppercase" data-testid={`nda-revision-${i}`}>
                    Rev. {n.revision} · angepasst
                  </span>
                )}
                {n.status === 'cancelled' && (
                  <span className="text-[9px] font-mono border border-red-600/40 bg-red-600/10 text-red-400 rounded px-1.5 py-0.5 uppercase">Cancelled</span>
                )}
              </div>
              <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                <StatusStep done icon={Send} label="Sent" ts={fmtDt(n.sent_at)} testid={`nda-status-sent-${i}`} />
                <StatusStep done={!!n.first_opened_at} icon={Eye}
                  label={n.first_opened_at ? `Opened${n.opened_count > 1 ? ` (${n.opened_count}x)` : ''}` : 'Opened'}
                  ts={fmtDt(n.first_opened_at)} testid={`nda-status-opened-${i}`} />
                <StatusStep done={!!n.signed_at} icon={n.signed_at ? CheckCircle2 : Clock} label="Signed"
                  ts={fmtDt(n.signed_at)} testid={`nda-status-signed-${i}`} />
                {n.reminder?.sent_at && (
                  <StatusStep done icon={BellRing} label="Erinnert" ts={fmtDt(n.reminder.sent_at)} testid={`nda-status-reminded-${i}`} />
                )}
                {n.status === 'signed' && !n.executed_email_sent && (
                  <span className="text-[9px] text-amber-400">⚠️ executed copy email failed — download &amp; send manually</span>
                )}
                <span className="ml-auto flex items-center gap-0.5">
                  <a href={`${API_BASE}/api/ndas/${n.id}/pdf`} target="_blank" rel="noreferrer" title="View / print PDF"
                    data-testid={`nda-pdf-${i}`} className="text-muted-foreground hover:text-teal-400 p-1"><FileDown className="w-3.5 h-3.5" /></a>
                  {n.status !== 'cancelled' && n.status !== 'signed' && (
                    <>
                      <button onClick={() => copyLink(n)} title="Copy public link" data-testid={`nda-copy-link-${i}`}
                        className="text-muted-foreground hover:text-teal-400 p-1"><Link2 className="w-3.5 h-3.5" /></button>
                      {isAdmin && (
                        <>
                          <button onClick={() => resend(n)} disabled={busy === n.id} title="Resend email" data-testid={`nda-resend-${i}`}
                            className="text-muted-foreground hover:text-sky-400 p-1"><RotateCw className="w-3.5 h-3.5" /></button>
                          <button onClick={() => cancel(n)} disabled={busy === n.id} title="Cancel NDA" data-testid={`nda-cancel-${i}`}
                            className="text-muted-foreground hover:text-red-400 p-1"><XCircle className="w-3.5 h-3.5" /></button>
                        </>
                      )}
                    </>
                  )}
                </span>
              </div>
              {n.reminder_due && (
                <div className="mt-2 flex items-center gap-2 flex-wrap border border-amber-500/30 bg-amber-500/10 rounded-sm px-2 py-1.5"
                  data-testid={`nda-reminder-due-${i}`}>
                  <BellRing className="w-3.5 h-3.5 text-amber-400 shrink-0" />
                  <span className="text-[11px] text-amber-200">
                    Seit {n.days_open} Tagen ohne Unterschrift{n.first_opened_at ? ' (geöffnet, nicht unterzeichnet)' : ' (noch nicht geöffnet)'}
                    {isAdmin ? ' — freundlich erinnern?' : ''}
                  </span>
                  {isAdmin && (
                    <span className="ml-auto flex items-center gap-1">
                      <Button size="sm" onClick={() => remind(n)} disabled={busy === n.id} data-testid={`nda-remind-${i}`}
                        className="bg-amber-600 hover:bg-amber-500 text-white rounded-sm text-[11px] h-7 px-2.5">
                        {busy === n.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Mail className="w-3 h-3 mr-1" /> Erinnerung senden</>}
                      </Button>
                      <button onClick={() => dismissReminder(n)} disabled={busy === n.id} title="Nicht erinnern"
                        data-testid={`nda-remind-dismiss-${i}`} className="text-muted-foreground hover:text-foreground p-1">
                        <BellOff className="w-3.5 h-3.5" />
                      </button>
                    </span>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {!isBrokered && (
        <NdaRemarksPanel projectId={projectId} isAdmin={isAdmin} hasNdas={ndas.length > 0} />
      )}
      <SendNdaDialog open={dialogOpen} onOpenChange={setDialogOpen} projectId={projectId}
        project={project} onSent={() => { load(); onChanged?.(); }} />
    </div>
  );
}
